import { AlertCircle, AlertTriangle, CheckCircle } from "lucide-react";
import { useTheme } from "next-themes";
import type * as React from "react";
import type { ToasterProps } from "sonner";
import { Toaster as Sonner, toast } from "sonner";

const TOAST_COLORS = {
  success: "#14A645",
  error: "#991212",
  info: "#CA8A04",
} as const;

function getToastColor(toastType: "success" | "error" | "info"): string {
  return TOAST_COLORS[toastType];
}

function getToastIcon(
  toastType: "success" | "error" | "info",
  color: string
): React.ReactElement {
  if (toastType === "success") {
    return <CheckCircle className="h-5 w-5 flex-shrink-0" color={color} />;
  }
  if (toastType === "error") {
    return <AlertTriangle className="h-5 w-5 flex-shrink-0" color={color} />;
  }
  return <AlertCircle className="h-5 w-5 flex-shrink-0" color={color} />;
}

type GenericToastProps = {
  message: string;
  toastType: "success" | "error" | "info";
  duration?: number;
  closeButton?: boolean;
};

const DEFAULT_TOAST_DURATION_MS = 4000; // Default 4 seconds

export function GenericToast(props: GenericToastProps) {
  const textColor = getToastColor(props.toastType);
  const icon = getToastIcon(props.toastType, textColor);
  const duration = props.duration ?? DEFAULT_TOAST_DURATION_MS;
  const closeButton = props.closeButton ?? false;

  const toastContent = (
    <div className="flex w-full items-center gap-3">
      {icon}
      <span
        className="font-semibold text-lg"
        data-toast-message="true"
        style={{ color: textColor }}
      >
        {props.message}
      </span>
    </div>
  );

  // Use standard Sonner methods for better reliability and auto-dismiss
  if (props.toastType === "success") {
    return toast.success(toastContent, {
      duration,
      icon: null,
      closeButton,
      className: "cart-toast success-toast",
      style: {
        border: "1px solid #e5e7eb",
        padding: "16px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      },
    });
  }

  if (props.toastType === "error") {
    return toast.error(toastContent, {
      duration,
      icon: null,
      closeButton,
      className: "cart-toast error-toast",
      style: {
        border: "1px solid #e5e7eb",
        padding: "16px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      },
    });
  }

  return toast.info(toastContent, {
    duration,
    icon: null,
    closeButton,
    className: "cart-toast info-toast",
    style: {
      border: "1px solid #e5e7eb",
      padding: "16px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    },
  });
}

export const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      className="toaster group"
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
        } as React.CSSProperties
      }
      theme={theme as ToasterProps["theme"]}
      {...props}
    />
  );
};
